import type { Channel, Me, Message, UserSummary } from '@novachat/shared';
import { desktop } from './desktop';
import { plainText } from './mentions';

const KEY = 'novachat.notify';
export const canNotify = typeof Notification !== 'undefined';

/** On when the browser allows it and the user has not switched it off here. */
export function notificationsOn(): boolean {
  if (!canNotify || Notification.permission !== 'granted') return false;
  try {
    return localStorage.getItem(KEY) !== 'off';
  } catch {
    return true;
  }
}

export async function setNotifications(on: boolean): Promise<boolean> {
  const allowed = on && canNotify && (Notification.permission === 'granted' || (await Notification.requestPermission()) === 'granted');
  try {
    localStorage.setItem(KEY, allowed ? 'on' : 'off');
  } catch {
    // storage unavailable: the browser permission alone decides
  }
  return allowed;
}

/**
 * Desktop notification for a new message (design §11), by the channel's level; never your own,
 * never for the channel you are looking at, never on Do Not Disturb.
 */
export function notifyAbout(message: Message, channel: Channel | undefined, me: Me, context: { dnd: boolean; viewing: boolean; people: readonly UserSummary[] }, onOpen: () => void) {
  if (!notificationsOn() || context.dnd || context.viewing || !channel || message.author?.id === me.id) return;
  const mentioned = message.mentions.everyone || message.mentions.userIds.includes(me.id);
  // The reader's choice for this channel (DMs default to every message, channels to mentions).
  if (channel.notifyLevel === 'mute' || (channel.notifyLevel === 'mentions' && !mentioned)) return;
  const author = message.kind === 'jira' ? 'Jira' : (message.author?.name ?? 'Biri');
  const body = plainText(message.content, context.people).replace(/\s+/g, ' ').slice(0, 160) || (message.attachments.length > 0 ? 'Dosya gönderdi' : '');
  show(channel.kind === 'dm' || channel.kind === 'jira' ? author : `${author} · #${channel.name}`, body, message.channelId, onOpen);
}

/** In the desktop app clicks come back by tag: remember what each one should open. */
const openers = new Map<string, () => void>();
desktop?.onNotificationClick?.((tag) => {
  openers.get(tag)?.();
  openers.delete(tag);
});

/** Never throws: phone browsers refuse page notifications (they need a service worker). */
function show(title: string, body: string, tag: string, onOpen: () => void): boolean {
  // The desktop app shows the operating system's own notification and brings its window forward.
  if (desktop?.notify) {
    openers.set(tag, onOpen);
    desktop.notify({ title, body, tag });
    return true;
  }
  try {
    const notification = new Notification(title, { body, tag, icon: '/icon-192.png' });
    notification.onclick = () => {
      window.focus();
      onOpen();
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
}

/** For the settings test button: if this does not appear, the operating system is holding it back. */
export const testNotification = () => show('NovaChat', 'Bildirimler çalışıyor.', 'novachat-test', () => {});
